import React from "react";
import { Box, Button, IconButton, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ReactMarkdown, { Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import {
  ChatMessageModel,
} from "../../client/SmartAssistantBackendClient";

type HistoryMessageProps = {
  msg: ChatMessageModel;
  effectiveContent?: string | null;
  hasRenderableContent: (value?: string | null) => boolean;
  normalizeContent: (value?: string | null) => string;
  markdownComponents: Components;
  openPdfMenu: (
    event: React.MouseEvent<HTMLButtonElement>,
    payload: { fileName: string; markdownContent: string }
  ) => void;
};

export function ChatHistoryMessage({
  msg,
  effectiveContent,
  hasRenderableContent,
  normalizeContent,
  markdownComponents,
  openPdfMenu,
}: HistoryMessageProps) {
  const clarificationQuestions = msg.clarification?.questions ?? [];
  const hasClarifications = msg.role === "user" && clarificationQuestions.length > 0;
  const [clarificationOpen, setClarificationOpen] = React.useState(false);

  return (
    <div className={`chat-bubble ${msg.role === "user" ? "right" : "left"}`}>
      {hasRenderableContent(effectiveContent) ? (
        <div className="chat-content">
          <ReactMarkdown
            remarkPlugins={[remarkGfm, remarkBreaks]}
            components={markdownComponents}
          >
            {normalizeContent(effectiveContent)}
          </ReactMarkdown>
        </div>
      ) : null}
      {hasClarifications ? (
        <Box sx={{ mt: 1 }}>
          <Box
            role="button"
            aria-expanded={clarificationOpen}
            onClick={() => setClarificationOpen(prev => !prev)}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              px: 1,
              py: 0.5,
              borderRadius: 1,
              border: "1px solid rgba(0,0,0,0.08)",
              cursor: "pointer",
              backgroundColor: "rgba(0,0,0,0.03)",
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              Klärungsfragen ({clarificationQuestions.length})
            </Typography>
            <IconButton size="small" sx={{ p: 0.25 }}>
              <ExpandMoreIcon
                fontSize="small"
                sx={{
                  transition: "transform 150ms ease",
                  transform: clarificationOpen ? "rotate(180deg)" : "rotate(0deg)",
                }}
              />
            </IconButton>
          </Box>
          {clarificationOpen ? (
            <Box sx={{ mt: 0.75, display: "grid", gap: 0.75 }}>
              {clarificationQuestions.map((q, idx) => {
                const key = q?.id ?? `clarification-${idx}`;
                const answer = q?.answer?.trim();
                return (
                  <Box key={key} sx={{ display: "grid", gap: 0.25 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {q?.question ?? `Frage ${idx + 1}`}
                    </Typography>
                    {answer ? (
                      <Typography variant="body2">{answer}</Typography>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        Keine Antwort angegeben.
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          ) : null}
        </Box>
      ) : null}
      {msg.role === "assistant" &&
        (msg.downloadRequest?.markdownContent ||
          hasRenderableContent(effectiveContent)) ? (
        <>
          <Box className="message-actions message-actions-top">
            <Button
              className="pdf-download-button"
              variant="text"
              size="small"
              onClick={(ev) => {
                const optimized = msg.downloadRequest?.markdownContent?.trim();
                const contentForPdf = optimized || (effectiveContent ?? "").trim();
                if (!contentForPdf) return;
                openPdfMenu(ev, {
                  fileName: optimized ? (msg.downloadRequest?.fileName ?? "export.pdf") : "export.pdf",
                  markdownContent: contentForPdf,
                });
              }}
            >
              PDF
            </Button>
          </Box>
          <Box className="message-actions message-actions-bottom">
            <Button
              className="pdf-download-button"
              variant="text"
              size="small"
              onClick={(ev) => {
                const optimized = msg.downloadRequest?.markdownContent?.trim();
                const contentForPdf = optimized || (effectiveContent ?? "").trim();
                if (!contentForPdf) return;
                openPdfMenu(ev, {
                  fileName: optimized ? (msg.downloadRequest?.fileName ?? "export.pdf") : "export.pdf",
                  markdownContent: contentForPdf,
                });
              }}
            >
              PDF
            </Button>
          </Box>
        </>
      ) : null}
    </div>
  );
}
